// src/components/layout/Navbar.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Link, useNavigate, NavLink, useLocation } from "react-router-dom";
import { FiShoppingCart, FiUser, FiMenu, FiX, FiBell, FiSearch } from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";
import API from "../../services/api";

const ANNOUNCEMENT_VERSION = "v1";
const BANNER_KEY = `mx_top_banner_closed_until_${ANNOUNCEMENT_VERSION}`;

const shouldShowBanner = () => {
  try {
    const until = Number(localStorage.getItem(BANNER_KEY) || 0);
    return Date.now() > until;
  } catch {
    return true;
  }
};

const hideBannerForHours = (hours = 24) => {
  try {
    const until = Date.now() + hours * 60 * 60 * 1000;
    localStorage.setItem(BANNER_KEY, String(until));
  } catch {}
};

const normalizeExternalUrl = (raw) => {
  const url = String(raw || "").trim();
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("//")) return `https:${url}`;
  return `https://${url}`;
};

const Navbar = ({ logoSrc: propLogoSrc }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);

  // ✅ texte de recherche (auto)
  const [search, setSearch] = useState("");

  const [activeHomeSection, setActiveHomeSection] = useState("");
  const [showBanner, setShowBanner] = useState(() => shouldShowBanner());

  const { user, logout } = useAuth();
  const { cartCount } = useCart();

  const profileRef = useRef(null);
  const mountedRef = useRef(false);

  const isAdmin = user?.role === "admin";
  const isMerchant = user?.role === "merchant";
  const isClient = !!user && !isAdmin && !isMerchant;

  const currentLogo = propLogoSrc || `${process.env.PUBLIC_URL || ""}/logo.png`;

  const maxulaHouseUrl = useMemo(() => {
    const raw = (process.env.REACT_APP_MAXULA_HOUSE_URL || process.env.REACT_APP_HOUSE_URL || "").trim();
    return normalizeExternalUrl(raw);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setProfileOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    let interval = null;
    if (user && isAdmin) {
      fetchNotificationCount();
      interval = setInterval(fetchNotificationCount, 30000);
    }
    return () => interval && clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAdmin]);

  const fetchNotificationCount = async () => {
    try {
      const res = await API.get("/admin/notifications/unread-count");
      if (!mountedRef.current) return;
      setUnreadNotifications(Number(res?.data?.count || 0));
    } catch {
      if (!mountedRef.current) return;
      setUnreadNotifications((prev) => prev || 0);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      navigate("/login");
      window.scrollTo(0, 0);
    }
  };

  const userName =
    (user && (user.firstName || user.name || (user.email ? user.email.split("@")[0] : null))) ||
    "Mon compte";

  // ✅ on empêche enter de faire quelque chose (mais bouton search marche toujours)
  const onSearchSubmit = (e) => {
    e.preventDefault();
  };

  // ✅ AUTO SEARCH (sans Enter) + debounce
  useEffect(() => {
    const q = search.trim();

    // si vide => on ne fait rien (ou tu peux décider de retourner vers /deals)
    if (!q) return;

    const timer = setTimeout(() => {
      navigate(`/deals?q=${encodeURIComponent(q)}&page=1`);
      setIsMenuOpen(false);
    }, 400);

    return () => clearTimeout(timer);
  }, [search, navigate]);

  const navLinkClass = ({ isActive }) =>
    `px-3 py-2 text-sm font-medium transition ${
      isActive ? "text-primary-600" : "text-gray-700 hover:text-primary-600"
    }`;

  const dealsBtnClass = (type) => {
    const params = new URLSearchParams(location.search);
    const sort = params.get("sort");
    const isActiveLink = location.pathname === "/deals" && sort === type;

    return `px-3 py-2 text-sm font-medium transition ${
      isActiveLink ? "text-primary-600" : "text-gray-700 hover:text-primary-600"
    }`;
  };

  const navBtnClass = (id) =>
    `px-3 py-2 text-sm font-medium transition ${
      location.pathname === "/" && activeHomeSection === id
        ? "text-primary-600"
        : "text-gray-700 hover:text-primary-600"
    }`;

  const scrollToSection = useCallback(
    (id) => {
      const el = document.getElementById(id);
      if (!el) return;
      const headerOffset = showBanner ? 120 : 90;
      const y = el.getBoundingClientRect().top + window.scrollY - headerOffset;
      window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
    },
    [showBanner]
  );

  const goHomeSection = (id) => (e) => {
    e.preventDefault();
    setIsMenuOpen(false);
    setProfileOpen(false);
    setActiveHomeSection(id);

    if (window.location.pathname === "/") {
      scrollToSection(id);
    } else {
      navigate("/");
      requestAnimationFrame(() => window.scrollTo(0, 0));
      setTimeout(() => scrollToSection(id), 120);
    }
  };

  useEffect(() => {
    if (location.pathname !== "/") setActiveHomeSection("");
  }, [location.pathname]);

  const closeBanner = () => {
    setShowBanner(false);
    hideBannerForHours(24);
  };

  return (
    <header className="sticky top-0 z-50">
      {showBanner && (
        <div className="bg-[#7a1147] text-white border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
            <div className="flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <span
                  className="hidden md:block text-sm leading-snug overflow-hidden"
                  style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}
                >
                  🎫 Validité des Coupons — Tous les coupons Maxula sont valables 60 jours à partir de la date d’achat.
                  Vous pouvez offrir le coupon (excellent cadeau). Après paiement, le nom + les coordonnées du partenaire
                  seront envoyés automatiquement. Tous les détails (adresse, conditions, validité) seront indiqués sur
                  le coupon reçu par WhatsApp. 📞 Contact : 0606 06 61 62 – 0539 34 24 94 (11h–20h). ⚠ Maxula décline
                  toute responsabilité : seul le partenaire est responsable.
                </span>
              </div>

              <div className="flex items-center gap-3 shrink-0">
                <Link
                  to="/annonces"
                  onClick={() => setIsMenuOpen(false)}
                  className="text-[11px] sm:text-sm font-semibold underline underline-offset-2 whitespace-nowrap"
                >
                  Lire plus
                </Link>

                <button
                  type="button"
                  onClick={closeBanner}
                  className="text-white/90 hover:text-white text-lg leading-none"
                  aria-label="Fermer"
                >
                  ×
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-16 flex items-center justify-between gap-3">
            <Link to="/" className="flex items-center shrink-0">
              <img
                src={currentLogo}
                alt="Maxula"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = `${process.env.PUBLIC_URL || ""}/logo.png`;
                }}
                className="h-10 w-auto object-contain"
              />
            </Link>

            {/* DESKTOP SEARCH */}
            <form onSubmit={onSearchSubmit} className="hidden md:flex flex-1 max-w-2xl">
              <div className="w-full relative">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher des deals..."
                  className="w-full pl-10 pr-12 py-2 rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-200"
                />
                <button
                  type="button"
                  onClick={() => {
                    const q = search.trim();
                    if (!q) return;
                    navigate(`/deals?q=${encodeURIComponent(q)}&page=1`);
                    setIsMenuOpen(false);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full
                             bg-[#e11d7b] text-white flex items-center justify-center
                             hover:bg-[#c91469] transition"
                  aria-label="Rechercher"
                >
                  <FiSearch />
                </button>
              </div>
            </form>

            <div className="hidden md:flex items-center gap-3">
              {user ? (
                <>
                  {isAdmin ? (
                    <Link
                      to="/admin/notifications"
                      className="relative p-2 text-gray-700 hover:text-primary-600"
                      aria-label="Notifications"
                    >
                      <FiBell size={22} />
                      {unreadNotifications > 0 && (
                        <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-bold text-white bg-red-500 rounded-full">
                          {unreadNotifications > 9 ? "9+" : unreadNotifications}
                        </span>
                      )}
                    </Link>
                  ) : (
                    <Link to="/cart" className="relative p-2 text-gray-700 hover:text-primary-600" aria-label="Panier">
                      <FiShoppingCart size={22} />
                      {cartCount > 0 && (
                        <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-bold text-white bg-primary-600 rounded-full">
                          {cartCount}
                        </span>
                      )}
                    </Link>
                  )}

                  <div className="relative" ref={profileRef}>
                    <button
                      type="button"
                      onClick={() => setProfileOpen((v) => !v)}
                      aria-expanded={profileOpen}
                      aria-haspopup="menu"
                      className="flex items-center gap-2 text-gray-700 hover:text-primary-600"
                    >
                      <FiUser size={20} />
                      <span className="font-medium">{userName}</span>
                    </button>

                    {profileOpen && (
                      <div
                        role="menu"
                        aria-orientation="vertical"
                        className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg py-2 border"
                      >
                        {isClient && (
                          <>
                            <Link
                              to="/dashboard"
                              onClick={() => setProfileOpen(false)}
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              Mon profil
                            </Link>
                            <Link
                              to="/dashboard/orders"
                              onClick={() => setProfileOpen(false)}
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              Mes commandes
                            </Link>
                            <Link
                              to="/dashboard/vouchers"
                              onClick={() => setProfileOpen(false)}
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              Mes vouchers
                            </Link>
                            <Link
                              to="/dashboard/favorites"
                              onClick={() => setProfileOpen(false)}
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              Mes favoris
                            </Link>
                          </>
                        )}

                        {isAdmin && (
                          <Link
                            to="/admin"
                            onClick={() => setProfileOpen(false)}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            Dashboard Admin
                          </Link>
                        )}

                        {isMerchant && (
                          <Link
                            to="/merchant"
                            onClick={() => setProfileOpen(false)}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            Dashboard Marchand
                          </Link>
                        )}

                        <button
                          type="button"
                          onClick={() => {
                            handleLogout();
                            setProfileOpen(false);
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                        >
                          Déconnexion
                        </button>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <Link to="/login" className="text-gray-700 hover:text-primary-600 font-medium">
                    Connexion
                  </Link>
                  <Link
                    to="/register"
                    className="px-4 py-2 rounded-full bg-primary-600 text-white hover:bg-primary-700 font-medium"
                  >
                    Inscription
                  </Link>
                </>
              )}
            </div>

            <button
              type="button"
              onClick={() => setIsMenuOpen((v) => !v)}
              className="md:hidden p-2 text-gray-700"
              aria-label="Ouvrir le menu"
            >
              {isMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
            </button>
          </div>

          {/* MOBILE SEARCH */}
          <form onSubmit={onSearchSubmit} className="md:hidden pb-3">
            <div className="w-full relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher des deals..."
                className="w-full pl-10 pr-12 py-2 rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-200"
              />
              <button
                type="button"
                onClick={() => {
                  const q = search.trim();
                  if (!q) return;
                  navigate(`/deals?q=${encodeURIComponent(q)}&page=1`);
                  setIsMenuOpen(false);
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full
                           bg-[#e11d7b] text-white flex items-center justify-center
                           hover:bg-[#c91469] transition"
                aria-label="Rechercher"
              >
                <FiSearch />
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="border-t bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="hidden md:flex items-center justify-between">
            <div className="flex items-center gap-1">
              <NavLink to="/" className={navLinkClass}>
                Accueil
              </NavLink>

              <button type="button" onClick={goHomeSection("categories")} className={navBtnClass("categories")}>
                Catégories
              </button>

              <button type="button" onClick={goHomeSection("cities")} className={navBtnClass("cities")}>
                Villes
              </button>

              <Link to="/deals?sort=newest" className={dealsBtnClass("newest")}>
                Nouveautés
              </Link>

              <Link to="/deals?sort=discount" className={dealsBtnClass("discount")}>
                Meilleures offres
              </Link>

              {maxulaHouseUrl && (
                <a
                  href={maxulaHouseUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="px-3 py-2 text-sm font-medium transition text-gray-700 hover:text-primary-600"
                >
                  Maxula House
                </a>
              )}
            </div>
          </div>

          {isMenuOpen && (
            <div className="md:hidden py-3 border-t">
              <div className="flex flex-col">
                <NavLink to="/" onClick={() => setIsMenuOpen(false)} className={navLinkClass}>
                  Accueil
                </NavLink>

                <button type="button" onClick={goHomeSection("categories")} className={`${navBtnClass("categories")} text-left`}>
                  Catégories
                </button>

                <button type="button" onClick={goHomeSection("cities")} className={`${navBtnClass("cities")} text-left`}>
                  Villes
                </button>

                <Link to="/deals?sort=newest" onClick={() => setIsMenuOpen(false)} className={dealsBtnClass("newest")}>
                  Nouveautés
                </Link>

                <Link to="/deals?sort=discount" onClick={() => setIsMenuOpen(false)} className={dealsBtnClass("discount")}>
                  Meilleures offres
                </Link>

                {maxulaHouseUrl && (
                  <a
                    href={maxulaHouseUrl}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => setIsMenuOpen(false)}
                    className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-primary-600"
                  >
                    Maxula House
                  </a>
                )}

                <div className="my-2 border-t" />

                {user ? (
                  <>
                    {!isAdmin && (
                      <Link to="/cart" onClick={() => setIsMenuOpen(false)} className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-primary-600">
                        Panier {cartCount > 0 && `(${cartCount})`}
                      </Link>
                    )}

                    {isAdmin && (
                      <Link to="/admin" onClick={() => setIsMenuOpen(false)} className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-primary-600">
                        Dashboard Admin
                      </Link>
                    )}

                    {isMerchant && (
                      <Link to="/merchant" onClick={() => setIsMenuOpen(false)} className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-primary-600">
                        Dashboard Marchand
                      </Link>
                    )}

                    <button
                      type="button"
                      onClick={() => {
                        handleLogout();
                        setIsMenuOpen(false);
                      }}
                      className="px-3 py-2 text-sm font-medium text-left text-red-600 hover:bg-red-50 rounded"
                    >
                      Déconnexion
                    </button>
                  </>
                ) : (
                  <div className="flex items-center gap-3 px-3 pt-2">
                    <Link to="/login" onClick={() => setIsMenuOpen(false)} className="text-gray-700 hover:text-primary-600 font-medium">
                      Connexion
                    </Link>
                    <Link to="/register" onClick={() => setIsMenuOpen(false)} className="px-4 py-2 rounded-full bg-primary-600 text-white hover:bg-primary-700 font-medium">
                      Inscription
                    </Link>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Navbar;
